import React, { useMemo } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useGetCustomerServiceRatesQuery } from "../store/services/userApi";

/* ------------------------------------------------------------------ */
/*  Nice labels / colours for services we already know about.          */
/*  Any service NOT listed here still renders automatically — its      */
/*  columns are detected from the data (see detectColumns below).      */
/* ------------------------------------------------------------------ */
const SERVICE_CONFIG = {
  SMS: {
    badgeBg: "rgba(230,241,251,0.7)",
    badgeColor: "#0C447C",
    dotColor: "#185FA5",
    columns: [
      { label: "Country", key: "country" },
      { label: "SMS rate", key: "sms_rates" },
      { label: "DLT rate", key: "dlt_rates" },
    ],
  },
  WhatsApp: {
    badgeBg: "rgba(225,245,238,0.7)",
    badgeColor: "#085041",
    dotColor: "#0F6E56",
    columns: [
      { label: "Country", key: "country" },
      { label: "Authentication", key: "authentication_price" },
      { label: "Marketing", key: "marketing_price" },
      { label: "Utility", key: "utility_price" },
    ],
  },
  RCS: {
    badgeBg: "rgba(238,237,254,0.7)",
    badgeColor: "#3C3489",
    dotColor: "#534AB7",
    columns: [
      { label: "Country", key: "country" },
      { label: "Media rate", key: "rcs_media_rate" },
      { label: "Text rate", key: "rcs_text_rate" },
    ],
  },
  CustomerData: {
    badgeBg: "rgba(254,243,226,0.8)",
    badgeColor: "#7A4A06",
    dotColor: "#D98A1F",
    columns: [
      { label: "Country", key: "country" },
      { label: "Data rate", key: "customer_data_rates" },
      { label: "Backup rate", key: "customer_backup_rates" },
      { label: "Billing cycle", key: "billing_cycle", type: "text" },
      { label: "Amount", key: "amount" },
    ],
  },
  Email: {
    badgeBg: "rgba(253,236,240,0.8)",
    badgeColor: "#8A1F3D",
    dotColor: "#C2315A",
    columns: [
      { label: "Country", key: "country" },
      { label: "Email rate", key: "email_rates" },
    ],
  },
};

// These services are always shown (even when empty), like before.
const ALWAYS_SHOW = ["SMS", "WhatsApp", "RCS"];

// Colours handed out to services we have no config for.
const FALLBACK_THEMES = [
  { badgeBg: "rgba(224,247,250,0.8)", badgeColor: "#0B5560", dotColor: "#1A9AAA" },
  { badgeBg: "rgba(243,232,255,0.8)", badgeColor: "#5B2A86", dotColor: "#8E4FC4" },
  { badgeBg: "rgba(255,237,224,0.8)", badgeColor: "#8A3B0B", dotColor: "#E0661F" },
  { badgeBg: "rgba(236,242,224,0.8)", badgeColor: "#41571A", dotColor: "#7A9B2E" },
];

// Keys on a rate object that describe the row, not a rate.
const META_KEYS = new Set([
  "customer_country_id",
  "country",
  "service",
  "smscroutes",
]);

/* ---------------------------- helpers ----------------------------- */
const isEmpty = (v) => v === null || v === undefined || v === "";
const isNonZero = (v) =>
  !isEmpty(v) && (Number.isNaN(Number(v)) || Number(v) !== 0);

const humanize = (key) => {
  const s = key.replace(/_/g, " ").trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
};

// Build columns from the data itself, for services without a config.
const detectColumns = (rows) => {
  const keys = [];
  rows.forEach((r) =>
    Object.keys(r).forEach((k) => {
      if (META_KEYS.has(k) || keys.includes(k)) return;
      if (r[k] !== null && typeof r[k] === "object") return; // skip nested objects
      keys.push(k);
    })
  );

  // Prefer fields that actually carry a value; if every value is 0/null,
  // fall back to fields that are at least present.
  let picked = keys.filter((k) => rows.some((r) => isNonZero(r[k])));
  if (!picked.length) picked = keys.filter((k) => rows.some((r) => !isEmpty(r[k])));

  return [
    { label: "Country", key: "country" },
    ...picked.map((k) => ({
      label: humanize(k),
      key: k,
      type: /cycle|period|status|type|name/i.test(k) ? "text" : "rate",
    })),
  ];
};

/* --------------------------- components --------------------------- */
const RateChip = ({ value }) => {
  if (isEmpty(value)) {
    return (
      <Typography component="span" sx={{ fontSize: "13px", color: "text.disabled" }}>
        —
      </Typography>
    );
  }
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        background: "rgba(255,255,255,0.45)",
        border: "0.5px solid rgba(0,0,0,0.12)",
        borderRadius: "6px",
        px: 1,
        py: "2px",
        fontSize: "12px",
        fontWeight: 600,
        fontVariantNumeric: "tabular-nums",
        color: "text.primary",
        backdropFilter: "blur(4px)",
      }}
    >
      ₹ {Number(value || 0).toFixed(4)}
    </Box>
  );
};

const ServiceBlock = ({ name, rows, fallbackIndex }) => {
  const cfg = SERVICE_CONFIG[name];
  const theme = cfg || FALLBACK_THEMES[fallbackIndex % FALLBACK_THEMES.length];
  const cols = cfg ? cfg.columns : detectColumns(rows);
  const colW = `${100 / cols.length}%`;

  return (
    <Box
      sx={{
        background: "rgba(255,255,255,0.18)",
        backdropFilter: "blur(8px)",
        border: "0.5px solid rgba(255,255,255,0.35)",
        borderRadius: "12px",
        overflow: "hidden",
        mb: 2,
      }}
    >
      {/* Section header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 2,
          py: 1.5,
          borderBottom: "0.5px solid rgba(255,255,255,0.25)",
          background: "#488df585",
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            background: theme.badgeBg,
            color: theme.badgeColor,
            fontSize: "12px",
            fontWeight: 600,
            px: 1.25,
            py: "3px",
            borderRadius: "20px",
            backdropFilter: "blur(4px)",
          }}
        >
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: theme.dotColor,
              flexShrink: 0,
            }}
          />
          {name}
        </Box>
        <Typography
          sx={{ ml: "auto", fontSize: "11px", color: "text.secondary" }}
        >
          {rows.length} {rows.length === 1 ? "country" : "countries"}
        </Typography>
      </Box>

      {/* Table (scrolls sideways if a service has many columns) */}
      <Box sx={{ overflowX: "auto" }}>
        <Box
          component="table"
          sx={{
            width: "100%",
            minWidth: cols.length > 4 ? cols.length * 130 : 0,
            borderCollapse: "collapse",
            tableLayout: "fixed",
          }}
        >
          <Box component="thead">
            <Box component="tr">
              {cols.map((c) => (
                <Box
                  key={c.key}
                  component="th"
                  sx={{
                    width: colW,
                    px: 2,
                    py: 1.1,
                    textAlign: "left",
                    fontSize: "10px",
                    fontWeight: 700,
                    color: "text.secondary",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    borderBottom: "0.5px solid rgba(255,255,255,0.2)",
                    background: "rgba(255,255,255,0.12)",
                  }}
                >
                  {c.label}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {rows.length === 0 ? (
              <Box component="tr">
                <Box
                  component="td"
                  colSpan={cols.length}
                  sx={{
                    px: 2,
                    py: 3,
                    textAlign: "center",
                    fontSize: "13px",
                    color: "text.disabled",
                  }}
                >
                  No data available
                </Box>
              </Box>
            ) : (
              rows.map((item, i) => (
                <Box
                  key={item.customer_country_id ?? i}
                  component="tr"
                  sx={{
                    transition: "background 0.15s",
                    "&:last-child td": { borderBottom: "none" },
                    "&:hover td": { background: "rgba(255,255,255,0.22)" },
                  }}
                >
                  {cols.map((c) => (
                    <Box
                      key={c.key}
                      component="td"
                      sx={{
                        width: colW,
                        px: 2,
                        py: 1.25,
                        fontSize: "13px",
                        borderBottom: "0.5px solid rgba(255,255,255,0.15)",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {c.key === "country" ? (
                        <Typography
                          sx={{
                            fontSize: "13px",
                            fontWeight: 500,
                            color: "text.primary",
                          }}
                        >
                          {item.country?.country_name || "—"}
                        </Typography>
                      ) : c.type === "text" ? (
                        <Typography sx={{ fontSize: "13px", color: "text.primary" }}>
                          {isEmpty(item[c.key]) ? "—" : String(item[c.key])}
                        </Typography>
                      ) : (
                        <RateChip value={item[c.key]} />
                      )}
                    </Box>
                  ))}
                </Box>
              ))
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const MyRates = () => {
  const { data, isLoading } = useGetCustomerServiceRatesQuery();

  // Group rows by service name — any service the API returns gets a block.
  const groups = useMemo(() => {
    const rates = data?.data || [];
    const map = new Map();
    ALWAYS_SHOW.forEach((n) => map.set(n, []));

    rates.forEach((r) => {
      const name = r.service?.service_name || "Other";
      if (!map.has(name)) map.set(name, []);
      map.get(name).push(r);
    });

    // Known services first (in config order), then new ones as they arrive
    const knownOrder = Object.keys(SERVICE_CONFIG).filter((n) => map.has(n));
    const extras = [...map.keys()].filter((n) => !SERVICE_CONFIG[n]);
    return [...knownOrder, ...extras].map((n) => [n, map.get(n)]);
  }, [data]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 8,
        }}
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  let fallbackIndex = -1;

  return (
    <Box
      sx={{
        bgcolor: "rgba(255,255,255,0.22)",
        backdropFilter: "blur(10px)",
        border: "0.5px solid rgba(255,255,255,0.3)",
        borderRadius: "16px",
        boxShadow: "0 4px 24px rgba(14,165,233,0.08)",
        maxWidth: 820,
        mx: "auto",
        p: { xs: 2, md: 3 },
      }}
    >
      <Typography
        sx={{
          fontSize: "17px",
          fontWeight: 700,
          color: "text.primary",
          mb: 0.5,
        }}
      >
        My rates
      </Typography>
      <Typography sx={{ fontSize: "13px", color: "text.secondary", mb: 2.5 }}>
        Pricing breakdown by service and country
      </Typography>

      {groups.map(([name, rows]) => {
        if (!SERVICE_CONFIG[name]) fallbackIndex += 1;
        return (
          <ServiceBlock
            key={name}
            name={name}
            rows={rows}
            fallbackIndex={fallbackIndex}
          />
        );
      })}
    </Box>
  );
};

export default MyRates;